import { CsrAPI } from "../../apis/CsrAPI";
import {
  fetchFigureNames,
  fetchTableNames,
  resetForStudy,
  setStatusError,
  setStatusPolling,
  setStatusReady,
} from "../slices/csrDataSlice";

// Module-level singleton, NOT useRef. A useRef map lives per component
// instance and cannot dedupe across the tree — that was the bug in
// useTableStatusPolling.
const intervals = new Map(); // studyId -> { intervalId, stop }

// Tracks studies for which the load pipeline has been kicked off in this
// JS context. The polling singleton dedupes while polling is running, but
// the moment polling stops (ready detected) there's a window where a
// re-entrant call could still slip past the slice's condition guards —
// particularly under React Strict Mode, multiple wizard mount points, or
// HMR. This Set guarantees one-shot start per study lifetime.
const startedStudies = new Set();

const DEFAULT_POLL_INTERVAL_MS = 5000;

const isCancellation = err =>
  err?.name === "CanceledError" ||
  err?.name === "AbortError" ||
  err?.code === "ERR_CANCELED";

export const isPolling = studyId => intervals.has(studyId);

const startPollingInternal = (studyId, dispatch, getState, intervalMs) => {
  if (intervals.has(studyId)) return;

  // `stopped` is the closure flag the singleton flips when stopPolling is
  // called. In-flight checks observe it and bail out so we never apply
  // a stale tick after the page has navigated away. `fetchesDispatched`
  // guards against the cycle firing tables/figures more than once even if
  // an interval tick raced past `stopPolling`.
  let stopped = false;
  let fetchesDispatched = false;

  const checkOnce = async () => {
    if (stopped || fetchesDispatched) return;
    try {
      const resp = await CsrAPI.getAllStatuses({ studyId }, true);
      if (stopped || fetchesDispatched) return;

      // Race guard: study changed mid-flight.
      if (getState().csrData.studyId !== studyId) {
        stopPolling(studyId);
        return;
      }

      const ready = resp?.study_files === true;
      if (ready) {
        fetchesDispatched = true;
        stopPolling(studyId);
        dispatch(setStatusReady());
        dispatch(fetchTableNames(studyId));
        dispatch(fetchFigureNames(studyId));
      }
    } catch (err) {
      if (stopped || isCancellation(err)) return;
      if (getState().csrData.studyId !== studyId) return;
      dispatch(setStatusError(err?.message || "Failed to check status"));
      // Keep polling — transient backend errors shouldn't strand the page.
    }
  };

  // Register the entry *before* dispatching anything, so that the first
  // `checkOnce` can correctly find and clear it via `stopPolling` if the
  // status response arrives synchronously / from cache.
  const entry = {
    intervalId: null,
    stop: () => {
      stopped = true;
    },
  };
  intervals.set(studyId, entry);
  entry.intervalId = setInterval(checkOnce, intervalMs);

  dispatch(setStatusPolling());
  checkOnce();
};

export const stopPolling = studyId => {
  const entry = intervals.get(studyId);
  if (!entry) return;
  clearInterval(entry.intervalId);
  entry.stop();
  intervals.delete(studyId);
};

export const stopAllPolling = () => {
  intervals.forEach(entry => {
    clearInterval(entry.intervalId);
    entry.stop();
  });
  intervals.clear();
  startedStudies.clear();
};

/**
 * Idempotent entry point. Dispatch from the page that owns the study
 * (the wizard questions page) to begin polling extraction status. Once
 * status flips ready, tables and figures are fetched once and hydrated
 * into the csrData slice. Safe to dispatch from multiple mount points;
 * the singleton guarantees a single interval per studyId.
 */
export const startStudyDataLoad =
  (studyId, options = {}) =>
  (dispatch, getState) => {
    if (!studyId) return;

    const intervalMs = options.intervalMs ?? DEFAULT_POLL_INTERVAL_MS;
    const slice = getState().csrData;

    if (slice.studyId && slice.studyId !== studyId) {
      // Tearing down the previous study cancels its polling and any
      // in-flight ticks via the closure flag. The previous study's
      // started-marker also goes — if the user comes back to it later,
      // a fresh load cycle is allowed.
      stopPolling(slice.studyId);
      startedStudies.delete(slice.studyId);
      dispatch(resetForStudy(studyId));
    } else if (slice.studyId == null) {
      dispatch(resetForStudy(studyId));
    }

    // Hard one-shot guard: if we've already initiated the pipeline for
    // this study in this JS context, do nothing — even if polling has
    // since finished and slice flags would otherwise allow a retry.
    if (startedStudies.has(studyId)) return;

    const refreshed = getState().csrData;

    // Polling already running — singleton dedupes.
    if (isPolling(studyId)) return;

    // Status flipped ready (fetches dispatched or done) or a fetch is
    // already in flight — don't kick off a fresh polling cycle that would
    // race the existing thunks. The thunks' own condition guards block
    // duplicates from there.
    if (
      refreshed.status === "ready" ||
      refreshed.tables.loading ||
      refreshed.figures.loading
    ) {
      startedStudies.add(studyId);
      return;
    }

    startedStudies.add(studyId);
    startPollingInternal(studyId, dispatch, getState, intervalMs);
  };
